"use client"

import { useCallback, useEffect, useState } from "react"
import { useWallet } from "@/lib/wallet-provider"
import { friendlyPaymentError, getWallet } from "@/lib/wallet"
import { signedFetch } from "@/lib/api-auth-client"
import { buildScoreLockingScript, buildScorePayload } from "@/lib/onchain-score"

export interface OnChainScoreRecord {
  txid: string
  score: number
  heroType: string
  levelIndex: number
  inscribedAt: string
}

export interface InscribeArgs {
  score: number
  heroType: string
  levelIndex: number
}

/**
 * Publish personal bests on-chain and list the ones already published.
 *
 * The write is a single 0-satoshi OP_RETURN output created by the player's own
 * BRC-100 wallet, so the wallet covers the mining fee and shows its own
 * confirmation prompt. After broadcast the txid is handed to the server, which
 * re-reads the transaction off the chain before storing it - the client's
 * claimed score is never trusted (see app/api/score/onchain/route.ts).
 */
export function useOnChainScores() {
  const { status, identityKey } = useWallet()
  const [records, setRecords] = useState<OnChainScoreRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInscribing, setIsInscribing] = useState(false)

  const refresh = useCallback(async () => {
    if (!identityKey) {
      setRecords([])
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(`/api/score/onchain?pubkey=${encodeURIComponent(identityKey)}`)
      if (res.ok) {
        const data = await res.json()
        setRecords(Array.isArray(data.records) ? data.records : [])
      }
    } catch {
      // Keep whatever we already listed on transient failures
    } finally {
      setIsLoading(false)
    }
  }, [identityKey])

  useEffect(() => {
    refresh()
  }, [refresh])

  const inscribe = useCallback(
    async (args: InscribeArgs): Promise<{ ok: boolean; txid?: string; error?: string }> => {
      if (status !== "connected" || !identityKey) {
        return { ok: false, error: "Connect your wallet to publish a record" }
      }
      setIsInscribing(true)
      try {
        const payload = buildScorePayload({
          identityKey,
          score: args.score,
          heroType: args.heroType,
          levelIndex: args.levelIndex,
          timestamp: Date.now(),
        })

        const { txid } = await getWallet().createAction({
          description: `Pelf best score: ${args.score.toLocaleString()}`,
          outputs: [
            {
              satoshis: 0,
              lockingScript: buildScoreLockingScript(payload),
              outputDescription: "Pelf score record",
            },
          ],
          options: { randomizeOutputs: false },
        })

        if (!txid) return { ok: false, error: "Your wallet did not return a transaction" }

        // Ask the server to verify + index it. A failure here does not undo the
        // on-chain record, so report it as a partial success rather than an error.
        const res = await signedFetch("/api/score/onchain", { txid }, identityKey)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          return {
            ok: true,
            txid,
            error:
              typeof data.error === "string"
                ? `Published on-chain, but not indexed yet: ${data.error}`
                : "Published on-chain, but not indexed yet",
          }
        }
        await refresh()
        return { ok: true, txid }
      } catch (e) {
        console.error("[onchain-score] inscribe failed:", e)
        return { ok: false, error: friendlyPaymentError(e) }
      } finally {
        setIsInscribing(false)
      }
    },
    [status, identityKey, refresh],
  )

  const bestOnChain = records.length > 0 ? records[0] : null

  return { records, bestOnChain, isLoading, isInscribing, inscribe, refresh }
}
